import React, { useMemo } from 'react';
import appBranding from '@happylife-a/branding';
import { translations, locales } from '@happylife-a/localization';
import utils from '@happylife-a/utils';
import webApp from '@happylife-a/web-app';
import webCore from '@happylife-a/web-core';
import { ChakraProvider } from '@chakra-ui/react';
import { AppDownloadVisibilityProvider } from '~contexts/AppDownloadModalContext';
import { ChatVisibilityProvider } from '~contexts/ChatVisibilityContext';
import { HeaderScrollableProvider } from '~contexts/HeaderScrollableContext';
import { MobileFooterSpacingProvider } from '~contexts/MobileFooterSpacingContext';
import BranchIoProvider from '~providers/BranchIoProvider';
import AgeGate from '~components/AgeGate';
import modals from './modals';
import getGuestScreens from './routes/guest';
import getPrivateScreens from './routes/private';
import getPublicScreens from './routes/public';

const getField = (field) => appBranding.configs.environments.getField(field);

const allowedUrls = [
  '/seller-privacy-policy',
  '/seller-terms-and-conditions',
  '/privacy-policy',
  '/terms-and-conditions',
  '/contact-us',
  '/about-us',
];

const localStorageConfigs = {
  encryptable: false, // @TODO: enable for production mode only
  secretKey: import.meta.env.VITE_SECURE_STORAGE_SECRET,
};

// Utility function to conditionally wrap children in a provider
const withConditionalProvider = (condition, ProviderComponent, children, props = {}) =>
  condition ? <ProviderComponent {...props}>{children}</ProviderComponent> : children;

export default function App({ RouterComponent, ...props }) {
  const routeList = webApp.routing.buildPrefixedMultiLangRoutes(
    { locales },
    {
      guest: getGuestScreens(),
      private: getPrivateScreens(),
      public: getPublicScreens(),
    }
  );

  const maintenance = useMemo(() => {
    if (utils.helpers.environment.isComingSoonMode()) {
      return webApp.providers.MAINTENANCE_TYPES.COMING_SOON;
    }
    return null;
  }, []);

  const enabledShoppingCartContext = appBranding.tools.features.isEnabled('ShoppingCartContext');

  const fieldsFirebase = appBranding.fields.env.Firebase;

  const firebaseConfigs = {
    projectId: getField(fieldsFirebase.Common.ProjectId),
    appId: getField(fieldsFirebase.Common.AppId),
    apiKey: getField(fieldsFirebase.Common.ApiKey),
    analyticsEnabled: getField(fieldsFirebase.Analytics.Enabled),
    measurementId: getField(fieldsFirebase.Analytics.MeasurementId),
    messagingSenderId: getField(fieldsFirebase.CloudMessaging.SenderId),
  };

  let content = (
    <RouterComponent {...props}>
      <ChatVisibilityProvider forceHidden={maintenance !== null}>
        <AppDownloadVisibilityProvider>
          <HeaderScrollableProvider>
            <MobileFooterSpacingProvider>
              <webApp.contexts.ModalProvider.ModalPortal />
              <webApp.components.ScrollToTop />

              <webApp.router.Routes>
                {routeList.map((route, index) => (
                  <webApp.router.Route
                    key={`AppRouteItem-${route.accessLevel}-${index}-${route.pathname}`}
                    path={route.pathname}
                    element={<webApp.routing.AppRouteItem {...route} loginUrl="/" />}
                  />
                ))}
              </webApp.router.Routes>
            </MobileFooterSpacingProvider>
          </HeaderScrollableProvider>
        </AppDownloadVisibilityProvider>
      </ChatVisibilityProvider>
    </RouterComponent>
  );

  content = withConditionalProvider(
    enabledShoppingCartContext,
    webCore.contexts.ShoppingCartProvider,
    content
  );

  content = (
    <webCore.contexts.FavoriteProvider>
      <webCore.contexts.LastViewedProductDetailsProvider>
        {content}
      </webCore.contexts.LastViewedProductDetailsProvider>
    </webCore.contexts.FavoriteProvider>
  );

  return (
    <BranchIoProvider>
      <webCore.providers.HappyLifeCoreProvider
        allowGuestUser={true}
        localStorage={localStorageConfigs}
        firebase={firebaseConfigs}
        errorComponent={(errorProps) => (
          <webApp.UiProvider provider={ChakraProvider}>
            <webApp.screens.Whoops {...errorProps} />
          </webApp.UiProvider>
        )}
      >
        <webApp.UiProvider provider={ChakraProvider}>
          <webApp.providers.HappyLifeWebProvider
            modals={modals}
            translations={translations}
            sentry={{
              dsn: import.meta.env.VITE_SENTRY_DSN,
              environment: 'Webapp',
            }}
            htmlColor={{
              start: webApp.theme.colors.white[50],
              end: appBranding.configs.environments.getField(
                appBranding.fields.brand.AppBranding.ThemeColor
              ),
            }}
          >
            <AgeGate>
              <webApp.providers.MaintenanceProvider
                allowedUrls={allowedUrls}
                maintenance={maintenance}
              >
                {content}
              </webApp.providers.MaintenanceProvider>
            </AgeGate>
          </webApp.providers.HappyLifeWebProvider>
        </webApp.UiProvider>
      </webCore.providers.HappyLifeCoreProvider>
    </BranchIoProvider>
  );
}
